import { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "@/types";
import { apiFetch, AUTH_TOKEN_KEY } from "@/services/api";

interface AuthContextValue {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem(AUTH_TOKEN_KEY),
  );
  const [isLoading, setIsLoading] = useState<boolean>(
    !!localStorage.getItem(AUTH_TOKEN_KEY),
  );

  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const maxRetries = 3;

    async function fetchMe(attempt: number) {
      try {
        const me = await apiFetch<User>("/auth/me");
        if (!cancelled) {
          setUser(me);
          setToken(storedToken);
          setIsLoading(false);
        }
      } catch (err) {
        const is401 =
          err instanceof Error && err.message.includes("401");
        // Retry on network/5xx errors (e.g. cold start), but not on 401
        if (!is401 && attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 2000 * attempt));
          if (!cancelled) return fetchMe(attempt + 1);
          return;
        }
        if (!cancelled) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          setToken(null);
          setUser(null);
          setIsLoading(false);
        }
      }
    }

    fetchMe(1);
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<{ access_token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    localStorage.setItem(AUTH_TOKEN_KEY, res.access_token);
    setToken(res.access_token);
    const me = await apiFetch<User>("/auth/me");
    setUser(me);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setToken(null);
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token && !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
